<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'

/**
 * A PDF's first page, small (D-223): drawn once it comes into view, so a long timeline of notes
 * does not read every file at once. Until then, and if it cannot be drawn, the PDF icon.
 */
const props = defineProps({ src: { type: String, required: true }, width: { type: Number, default: 96 } })
const host = ref(null)
const drawn = ref(false)
let seen = null

async function draw() {
  try {
    const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs')
    const worker = await import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url')
    pdfjs.GlobalWorkerOptions.workerSrc = worker.default
    const pdf = await pdfjs.getDocument({ url: props.src, withCredentials: true }).promise
    const page = await pdf.getPage(1)
    const ratio = window.devicePixelRatio || 1
    const base = page.getViewport({ scale: 1 })
    const viewport = page.getViewport({ scale: (props.width / base.width) * ratio })
    const canvas = document.createElement('canvas')
    canvas.width = viewport.width
    canvas.height = viewport.height
    canvas.style.width = `${props.width}px`
    canvas.className = 'block bg-white'
    await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise
    if (!host.value) return
    host.value.replaceChildren(canvas)
    drawn.value = true
    pdf.destroy()
  } catch {
    // the icon stays
  }
}
onMounted(() => {
  if (typeof IntersectionObserver === 'undefined') return draw()
  seen = new IntersectionObserver((entries) => {
    if (entries.some((e) => e.isIntersecting)) {
      seen.disconnect()
      draw()
    }
  }, { rootMargin: '200px' })
  seen.observe(host.value)
})
onBeforeUnmount(() => seen?.disconnect())
</script>

<template>
  <div ref="host" :style="{ width: `${width}px`, minHeight: drawn ? null : `${Math.round(width * 1.3)}px` }" :data-drawn="drawn || null" class="flex items-center justify-center overflow-hidden bg-[var(--surface-2)]">
    <i class="fa-solid fa-file-pdf text-3xl text-red-500" aria-hidden="true"></i>
  </div>
</template>
